import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Music, ThumbsUp, ThumbsDown, Plus, User, Check, X, Clock } from 'lucide-react';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import { canManageQueue } from '../utils/roles';

const Suggestions = ({ session, onShowSearch }) => {
  const { voteSong, hostAction } = useSocket();
  const { user } = useAuth();

  // Hosts AND co-hosts can approve/reject suggestions
  const isHost = canManageQueue(session, user?.uid);
  // Optimistic vote overrides until the server round-trip lands
  const [localVotes, setLocalVotes] = useState({});

  const handleVote = (songId, vote) => {
    const current = localVotes[songId] ?? serverVoteFor(songId);
    const next = current === vote ? 0 : vote;
    setLocalVotes((prev) => ({ ...prev, [songId]: next }));
    voteSong(songId, next);
  };
  
  const handleApproveSong = (suggestionId) => {
    console.log('[Suggestions] Approving song:', {
      suggestionId,
      allSuggestions: session.suggestions,
      sessionId: session.id
    });
    hostAction('approveSong', null, suggestionId);
  };
  
  const handleRemoveSong = (suggestionId) => {
    hostAction('removeSong', null, suggestionId);
  };

  // One tap clears the backlog — a busy host shouldn't be a bottleneck
  const handleApproveAll = () => {
    Object.keys(session.suggestions || {}).forEach((key, i) => {
      // Stagger slightly so each approval lands cleanly
      setTimeout(() => hostAction('approveSong', null, key), i * 250);
    });
  };

  const serverVoteFor = (songId) => session.votes?.[songId]?.[user?.uid] || 0;

  // Vote counts live in session.votes (same as the playlist), with the
  // user's own optimistic vote layered on top
  const suggestions = Object.entries(session.suggestions || {}).map(([key, value]) => {
    const songVotes = session.votes?.[key] || {};
    let up = 0;
    let down = 0;
    Object.values(songVotes).forEach((v) => {
      if (v === 1) up += 1;
      else if (v === -1) down += 1;
    });

    const serverVote = songVotes[user?.uid] || 0;
    const localVote = localVotes[key];
    const myVote = localVote !== undefined ? localVote : serverVote;
    if (localVote !== undefined && localVote !== serverVote) {
      if (serverVote === 1) up -= 1;
      else if (serverVote === -1) down -= 1;
      if (localVote === 1) up += 1;
      else if (localVote === -1) down += 1;
    }

    return {
      ...value,
      firebaseKey: key,
      upvotes: up,
      downvotes: down,
      myVote,
      isMine: value.suggestedBy?.uid === user?.uid
    };
  });

  if (suggestions.length === 0) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Music className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No suggestions yet</h3>
          <p className="text-gray-400 mb-6">
            Be the first to suggest a song!
          </p>
          <button
            onClick={onShowSearch}
            className="btn-primary flex items-center mx-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Suggest Song
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2 gap-2">
        <h2 className="text-xl font-semibold">Song Suggestions</h2>
        <div className="flex items-center gap-2 shrink-0">
          {isHost && suggestions.length > 1 && (
            <button
              onClick={handleApproveAll}
              className="flex items-center px-3 py-2 rounded-lg bg-green-600/20 text-green-300 text-sm font-medium hover:bg-green-600/30 transition-colors"
            >
              <Check className="w-4 h-4 mr-1.5" />
              Approve all
            </button>
          )}
          <button
            onClick={onShowSearch}
            className="btn-primary flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Suggest Song
          </button>
        </div>
      </div>
      <p className="text-sm text-gray-400 mb-6">
        {isHost
          ? 'Approve songs to add them to the playlist, or remove the ones that don\'t fit. A guest\'s first approved song makes them trusted — their next picks skip the line.'
          : 'Songs here are waiting for the host\'s approval. Vote to show your support!'}
      </p>
      
      <div className="space-y-3">
        {suggestions.map((suggestion, index) => (
          <motion.div
            key={suggestion.firebaseKey}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-purple-500 transition-colors"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center space-x-4 flex-1 min-w-0">
                {/* Song Image */}
                {suggestion.song.image && (
                  <img
                    src={suggestion.song.image}
                    alt={suggestion.song.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                
                {/* Song Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate flex items-center gap-2">
                    <span className="truncate min-w-0">{suggestion.song.name}</span>
                    {suggestion.isMine && (
                      <span className="text-[10px] font-bold bg-purple-600 text-white px-1.5 py-0.5 rounded shrink-0">
                        YOURS
                      </span>
                    )}
                  </h3>
                  <p className="text-gray-400 text-sm truncate">
                    {suggestion.song.artist} • {suggestion.song.album}
                  </p>
                  <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400 flex-wrap">
                    <span className="flex items-center">
                      <User className="w-3 h-3 mr-1" />
                      {suggestion.suggestedBy.displayName}
                    </span>
                    <span className={suggestion.upvotes - suggestion.downvotes > 0 ? 'text-green-400' : ''}>
                      {suggestion.upvotes - suggestion.downvotes > 0 ? '+' : ''}{suggestion.upvotes - suggestion.downvotes} votes
                    </span>
                    <span className="flex items-center text-yellow-500/80">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending approval
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2 ml-2 shrink-0">
                {isHost ? (
                  /* Host Controls */
                  <>
                    <button
                      onClick={() => handleApproveSong(suggestion.firebaseKey)}
                      className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                      title="Add to Playlist"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveSong(suggestion.firebaseKey)}
                      className="p-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                      title="Remove Suggestion"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  /* Guest Vote Controls */
                  <>
                    <button
                      onClick={() => handleVote(suggestion.firebaseKey, 1)}
                      className={`p-2 rounded-lg transition-colors ${
                        suggestion.myVote === 1
                          ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                          : 'bg-gray-600 text-gray-300 hover:bg-green-600/30 hover:text-green-300'
                      }`}
                      title="Upvote"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleVote(suggestion.firebaseKey, -1)}
                      className={`p-2 rounded-lg transition-colors ${
                        suggestion.myVote === -1
                          ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                          : 'bg-gray-600 text-gray-300 hover:bg-red-600/30 hover:text-red-300'
                      }`}
                      title="Downvote"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Suggestions; 